import moment, { Moment } from 'moment';
import { Duration } from 'moment/moment';
import {
	DataTableRequestFilter,
	TableColumnDefinition,
} from '~/components/acurity-table/acurity-table';
import { FilterType } from '~/components/filter/filter-options';
import { DateFormatValueConverter } from '~/converters/date-format';
import { DataTypeEnum } from '~/models/data-type';
import { FilterModel } from '~/models/filter-model';
import { LocalizedDateTimeFormats } from '~/utils/datetime/localized-date-time-formats';

/**
 * To update the date/timestamp filter value to a range
 * to meet the backend logic
 */
export class DataTableRequestFilterDateTimeAdapter {
	private column: TableColumnDefinition<string>;
	private filter: FilterModel;

	constructor(column: TableColumnDefinition<string>) {
		this.column = column;
		this.filter = column.filter;
	}

	/**
	 * Update the filter values
	 */
	public updateFilterValues(requestFilter: DataTableRequestFilter): void {
		if (
			!this.filter.value ||
			!this.column.dateTimeProperties ||
			!(
				this.column.dataType.name === DataTypeEnum.Date ||
				this.column.dataType.name === DataTypeEnum.Timestamp
			)
		) {
			return;
		}

		this.updateForEqualsOperation(requestFilter);
		this.updateForGreaterThanOperation(requestFilter);
	}

	/**
	 * Convert {@link FilterType.EQUALS} to a date range to align the backend logic {@link DateEqualsFilter}
	 */
	private updateForEqualsOperation(
		requestFilter: DataTableRequestFilter,
	): void {
		if (FilterType.EQUALS !== this.filter.operation.value) {
			return;
		}

		const dateMoment: Moment = this.getDateMoment();
		const duration: Duration = this.getUnitDuration();
		if (duration && dateMoment) {
			requestFilter.upperValue = dateMoment
				.add(duration)
				.format(DateFormatValueConverter.apiFormat);
		}
		requestFilter.operation = FilterType.BETWEEN;
	}

	/**
	 * Convert {@link FilterType.GREATER_THAN} to a date range to align the backend logic {@link DateAfterFilter}
	 */
	private updateForGreaterThanOperation(
		requestFilter: DataTableRequestFilter,
	): void {
		if (FilterType.GREATER_THAN !== this.filter.operation.value) {
			return;
		}
		const dateMoment: Moment = this.getDateMoment();
		const duration: Duration = this.getUnitDuration();
		if (duration && dateMoment) {
			requestFilter.value = dateMoment
				.add(duration)
				.format(DateFormatValueConverter.apiFormat);
		}
	}

	/**
	 * Get {@link DateTimeProperties.format} from the column
	 */
	private getFormat(): LocalizedDateTimeFormats {
		return this.column.dateTimeProperties.format;
	}

	/**
	 * Convert string | string[] to Moment, string[] is not applied to date/timestamp
	 */
	private getDateMoment(): Moment {
		const filterValue: string = Array.isArray(this.filter.value)
			? ''
			: this.filter.value;

		return DateFormatValueConverter.toMoment(filterValue);
	}

	/**
	 * Get Moment unit based on format
	 * See the possible value from https://momentjscom.readthedocs.io/en/latest/moment/03-manipulating/01-add/
	 */
	private getUnitDuration(): Duration {
		const unit: number = 1;
		switch (this.getFormat()) {
			case LocalizedDateTimeFormats.Date:
			case LocalizedDateTimeFormats.DateShort:
				return moment.duration({ days: unit });
			case LocalizedDateTimeFormats.DateTime:
				return moment.duration({ minutes: unit });
			case LocalizedDateTimeFormats.DateTimeWithSeconds:
				return moment.duration({ seconds: unit });
			// ignore Time and TimeWithSecond as no implementation for them
			case LocalizedDateTimeFormats.Time:
			case LocalizedDateTimeFormats.TimeWithSeconds:
			default:
				return null;
		}
	}
}
